import React, { type PropsWithChildren, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';

import styled from 'styled-components';

import { useAuth } from '@sine/auth-client';
import { ApplicationFrame, type IAppVersion, type ISidebarLink } from '@sine/sine-application-frame';
import { type INavBarActionProps, Text, colors, spacing } from '@sine/sine-ui-components';
import UICVersion from '@sine/sine-ui-components/version';

import apiVersionService from './external/apiVersion/apiVersion.service';
import HelpModal from './help/helpModal';
import CreateInvitationModal from './pages/Invitations/components/CreateInvitationModal';
import PreLoginFrame from './pre-login/components/PreLoginFrame';
import { useLinks } from './routing/LinkProvider';
import {
  HIDDEN_APP_FRAME_ROUTES,
  PRE_LOGIN_FRAME_ROUTES,
  ROUTE_ID,
  ROUTE_PATH,
  SIDEBAR_ROUTES,
  TOP_LEVEL_LINKS,
} from './routing/routeConfig';
import rxEventHub, { RxEvent } from './services/events/rx';
import { Container } from './shared/components';
import { BannerComponent } from './shared/components/ui';
import wrapLazyLoad from './shared/hoc/wrapLazyLoad';
import VisitorCheckInModal from './visit/signin/components/visitorCheckInModal';

const GlobalMessagingModal = wrapLazyLoad(lazy(() => import('./pages/Messaging/GlobalMessagingModal')));

const MainContent = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: scroll;
  background-color: ${colors.grey100};
`;

const StyledContentWrapDiv = styled.div`
  padding-bottom: ${spacing(5)};
`;

const StyledContainer = styled(Container)`
  padding: ${spacing(5)} 0;
`;

const StyledFooterText = styled(Text)`
  margin-left: ${spacing(2.5)};
`;

function getHighlightedPage() {
  const currentPage = window.location.pathname;

  if (currentPage === '/' || currentPage === '/activity') {
    return 'Activity';
  } else {
    for (const [key, value] of TOP_LEVEL_LINKS) {
      if (currentPage.includes(key)) {
        return value;
      }
    }
  }
}

function formatAutomationTag(tag: string) {
  return tag.toLowerCase().replace(/ +/g, '-');
}

const HIDDEN_APP_FRAME_PATHS = HIDDEN_APP_FRAME_ROUTES.map((route) => route.path);
const PRE_LOGIN_FRAME_PATHS = PRE_LOGIN_FRAME_ROUTES.map((route) => route.path);

type CoreAppFrameProps = PropsWithChildren<{
  isCoreEnabled: boolean;
  isSysAdmin: boolean;
}>;

export default function CoreAppFrame({ children, isCoreEnabled, isSysAdmin }: CoreAppFrameProps) {
  const history = useHistory();
  const { userDetails } = useAuth();
  const { routes, products, navBarPermissions } = useLinks();
  const location = useLocation();
  const [showCreateInvitationModal, setShowCreateInvitationModal] = useState(false);
  const [showVisitorCheckInModal, setShowVisitorCheckInModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const initialActivePage = useMemo(() => getHighlightedPage(), []);
  const [activePage, setActivePage] = useState(initialActivePage);
  const scrollRef = useRef<HTMLDivElement>(null);

  const hideAppFrame = matchPath(location.pathname, { path: HIDDEN_APP_FRAME_PATHS });
  const showPreLoginFrame = matchPath(location.pathname, { path: PRE_LOGIN_FRAME_PATHS });
  const showPreLoginBackground = !matchPath(location.pathname, { path: ROUTE_PATH.SIGNUP_SITE });

  const versions = useMemo<Array<IAppVersion>>(
    () =>
      [
        {
          name: 'core-api',
          value: apiVersionService.getApiVersion('core'),
        },
        {
          name: 'core-client',
          value: window.env.REACT_APP_CLIENT_VERSION,
        },
        {
          name: 'sine-ui-components',
          value: UICVersion,
        },
      ] satisfies Array<IAppVersion>,
    [],
  );

  const menuItems = useMemo<Array<ISidebarLink>>(() => {
    const createSidebarLink = (route: (typeof routes)[number]): ISidebarLink => ({
      'icon': route.iconName,
      'label': route.label,
      'data-st-ext': `sidebar-core-${formatAutomationTag(route.label)}`,
      'onClick': () => {
        history.push(route.path);
        setActivePage(route.label);
        scrollRef.current?.scrollTo(0, 0);
      },
    });

    return (
      isCoreEnabled || isSysAdmin
        ? // Core routes
          routes.map(createSidebarLink)
        : // Supplier routes
          SIDEBAR_ROUTES.filter((route) => route.id === ROUTE_ID.MY_PASSES).map(createSidebarLink)
    ) satisfies Array<ISidebarLink>;
  }, [routes, isCoreEnabled, isSysAdmin, history]);

  useEffect(() => {
    const unlisten = history.listen(() => {
      setActivePage(getHighlightedPage());
    });

    return () => unlisten();
  }, [history]);

  const navBarActions = useMemo<Array<INavBarActionProps>>(() => {
    const navBarButtons: Array<INavBarActionProps> = [];

    if (navBarPermissions.invitations) {
      navBarButtons.push({
        'icon': 'invitations',
        'onClick': () => setShowCreateInvitationModal(true),
        'buttonText': 'Invite guest',
        'data-st-ext': 'nav-bar-invite-guest',
      });
    }

    if (navBarPermissions.messages) {
      navBarButtons.push({
        'icon': 'messaging',
        'onClick': () => setShowMessageModal(true),
        'buttonText': 'Messaging',
        'data-st-ext': 'nav-bar-messaging',
      });
    }

    if (navBarPermissions.checkIn) {
      navBarButtons.push({
        'icon': 'arrow-check-in',
        'onClick': () => setShowVisitorCheckInModal(true),
        'buttonText': 'Check-in',
        'variant': 'primary',
        'data-st-ext': 'nav-bar-check-in',
      });
    }

    return navBarButtons;
  }, [navBarPermissions]);

  const thisYear = useMemo(() => new Date().getFullYear(), []);

  const handleBrandClick = useCallback(() => {
    const activitiesRoute = routes.find((route) => route.id === ROUTE_ID.ACTIVITY);
    if (!activitiesRoute) return; // Should never happen, but just in case and makes TypeScript happy
    history.push(activitiesRoute.path);
    setActivePage(activitiesRoute.label);
    scrollRef.current?.scrollTo(0, 0);
  }, [history, routes]);

  const handleLogoutClick = useCallback(() => {
    rxEventHub.broadcast(RxEvent.LOGOUT, {});
  }, []);

  const handleProfileClick = useCallback(() => {
    // clear the route highlighting when we go to settings.
    setActivePage('');
    history.push(ROUTE_PATH.USER_SETTINGS);
    scrollRef.current?.scrollTo(0, 0);
  }, [history]);

  return (
    <>
      {hideAppFrame ? (
        children
      ) : showPreLoginFrame ? (
        <PreLoginFrame user={userDetails} showBackground={showPreLoginBackground}>
          {children}
        </PreLoginFrame>
      ) : (
        <ApplicationFrame
          activePage={activePage}
          productLinks={products}
          productBrand='core'
          versionInfo={versions}
          actionLinks={menuItems}
          navBarActions={isCoreEnabled || isSysAdmin ? navBarActions : []}
          onHelpClick={() => setShowHelpModal(true)}
          onProfileClick={handleProfileClick}
          onLogOutClick={handleLogoutClick}
          onProductBrandClick={handleBrandClick}
        >
          <MainContent ref={scrollRef} data-st-ext='div-app-frame-main-content'>
            <StyledContentWrapDiv>
              {isCoreEnabled && <BannerComponent />}
              {!!routes.length && children}
            </StyledContentWrapDiv>

            {showVisitorCheckInModal && (
              <VisitorCheckInModal onClose={() => setShowVisitorCheckInModal(false)} show={true} />
            )}

            {showCreateInvitationModal && (
              <CreateInvitationModal onClose={() => setShowCreateInvitationModal(false)} show={true} />
            )}

            {showMessageModal && <GlobalMessagingModal onClose={() => setShowMessageModal(false)} />}

            <HelpModal show={showHelpModal} closeModal={() => setShowHelpModal(false)} />

            <StyledContainer>
              <StyledFooterText variant='small' colorVariant='text68' data-st-ext='footer-client-version'>
                Copyright. Sine Group Pty Ltd &copy; {thisYear} - {window.env.REACT_APP_CLIENT_VERSION}
              </StyledFooterText>
            </StyledContainer>
          </MainContent>
        </ApplicationFrame>
      )}
    </>
  );
}
